/**
 * Split text + scale appear effects via Motion (motion.dev).
 *
 * Split (Prana bouncy heading):
 *   data-split-text-appear="true" — per-word opacity + scale spring
 *
 * Scale (button in Prana bouncy):
 *   data-scale-appear="true" — opacity linear + scale ease-out expo
 *
 * Respects prefers-reduced-motion: static reveal, no animation.
 */
const SPLIT_SELECTOR = '[data-split-text-appear="true"]';
const SCALE_SELECTOR = '[data-scale-appear="true"]';

const SPRING_DURATION_S = 0.7;
const SPRING_BOUNCE = 0.8;
const STAGGER_S = 0.05;
const START_DELAY_S = 0.35;

/**
 * @param {HTMLElement} el
 * @returns {number}
 */
function getStartDelayS(el) {
  const raw = el.dataset.appearStartDelay;
  if (raw == null || raw === "") return START_DELAY_S;
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : START_DELAY_S;
}

const SCALE_DURATION_S = 0.8;
const EASE_EXPO_OUT = [0.19, 1, 0.22, 1];

const INVIEW_AMOUNT = "some";
const INVIEW_MARGIN = "0px 0px -10% 0px";

/** @type {WeakSet<HTMLElement>} */
const boundSplitElements = new WeakSet();
/** @type {WeakSet<HTMLElement>} */
const boundScaleElements = new WeakSet();
/** @type {WeakMap<HTMLElement, () => void>} */
const cleanups = new WeakMap();

/** @param {() => void} fn */
function whenReady(fn) {
  if (document.readyState !== "loading") fn();
  else document.addEventListener("DOMContentLoaded", fn);
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * @param {Text} textNode
 * @returns {HTMLElement[]}
 */
function splitTextNode(textNode) {
  const value = textNode.nodeValue || "";
  if (!value.trim()) return [];

  const frag = document.createDocumentFragment();
  /** @type {HTMLElement[]} */
  const items = [];
  const tokens = value.split(/(\s+)/);

  for (const token of tokens) {
    if (token === "") continue;
    if (/^\s+$/.test(token)) {
      frag.appendChild(document.createTextNode(token));
      continue;
    }

    const span = document.createElement("span");
    span.className = "split-text__word";
    span.textContent = token;
    frag.appendChild(span);
    items.push(span);
  }

  const parent = textNode.parentNode;
  if (parent) parent.replaceChild(frag, textNode);
  return items;
}

/**
 * @param {HTMLElement} root
 * @returns {HTMLElement[]}
 */
function splitElement(root) {
  /** @type {Text[]} */
  const textNodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !node.nodeValue.trim()) {
        return NodeFilter.FILTER_REJECT;
      }
      const parent = /** @type {Element | null} */ (node.parentElement);
      if (parent && parent.closest("script,style,noscript")) {
        return NodeFilter.FILTER_REJECT;
      }
      if (parent && parent.classList.contains("split-text__word")) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n = walker.nextNode();
  while (n) {
    textNodes.push(/** @type {Text} */ (n));
    n = walker.nextNode();
  }

  /** @type {HTMLElement[]} */
  const items = [];
  for (const tn of textNodes) {
    items.push(...splitTextNode(tn));
  }
  return items;
}

/**
 * @param {HTMLElement[]} items
 */
function setInitialHidden(items) {
  for (const el of items) {
    el.style.opacity = "0";
    el.style.transform = "scale(0.8)";
    el.style.willChange = "opacity, transform";
  }
}

/**
 * @param {HTMLElement[]} items
 */
function revealStaticItems(items) {
  for (const el of items) {
    el.style.opacity = "1";
    el.style.transform = "none";
    el.style.willChange = "";
  }
}

/**
 * @param {HTMLElement} el
 */
function revealStaticElement(el) {
  el.style.opacity = "1";
  el.style.transform = "none";
  el.style.willChange = "";
}

/**
 * @param {HTMLElement} el
 */
function initSplitElement(el) {
  if (boundSplitElements.has(el)) return;
  boundSplitElements.add(el);

  const items = splitElement(el);
  if (items.length === 0) return;

  if (prefersReducedMotion()) {
    revealStaticItems(items);
    el.dataset.splitTextReady = "true";
    return;
  }

  setInitialHidden(items);
  el.dataset.splitTextReady = "true";

  const startDelayS = getStartDelayS(el);

  /** @type {() => void} */
  let cleanup = () => {};

  import("@theme/motion")
    .then((motion) => {
      const { inView, animate, stagger } = motion;

      cleanup = inView(
        el,
        () => {
          animate(
            items,
            { opacity: [0, 1], scale: [0.8, 1] },
            {
              type: "spring",
              duration: SPRING_DURATION_S,
              bounce: SPRING_BOUNCE,
              delay: stagger(STAGGER_S, { startDelay: startDelayS }),
            },
          );
          const settleMs =
            (startDelayS + SPRING_DURATION_S + items.length * STAGGER_S) *
              1000 +
            200;
          window.setTimeout(() => {
            revealStaticItems(items);
          }, settleMs);
          return undefined;
        },
        {
          amount: INVIEW_AMOUNT,
          margin: INVIEW_MARGIN,
        },
      );
    })
    .catch(() => {
      revealStaticItems(items);
    });

  cleanups.set(el, () => {
    cleanup();
    boundSplitElements.delete(el);
  });
}

/**
 * @param {HTMLElement} el
 * @param {(target: Element, keyframes: object, options?: object) => unknown} animate
 * @param {number} startDelayS
 */
function runScaleAppearAnimation(el, animate, startDelayS) {
  const options = {
    duration: SCALE_DURATION_S,
    delay: startDelayS,
  };

  if (el.classList.contains("button")) {
    animate(el, { opacity: [0, 1] }, { ...options, ease: "linear" });
    animate(el, { scale: [0.8, 1] }, { ...options, ease: EASE_EXPO_OUT });
    return;
  }

  animate(
    el,
    { opacity: [0, 1], scale: [0.8, 1] },
    { ...options, ease: EASE_EXPO_OUT },
  );
}

/**
 * @param {HTMLElement} el
 */
function initScaleElement(el) {
  if (boundScaleElements.has(el)) return;
  boundScaleElements.add(el);

  if (prefersReducedMotion()) {
    revealStaticElement(el);
    el.dataset.scaleAppearReady = "true";
    return;
  }

  el.style.opacity = "0";
  el.style.transform = "scale(0.8)";
  el.style.willChange = "opacity, transform";
  el.style.transformOrigin = "50% 60%";
  el.dataset.scaleAppearReady = "true";

  const startDelayS = getStartDelayS(el);

  /** @type {() => void} */
  let cleanup = () => {};

  import("@theme/motion")
    .then((motion) => {
      const { inView, animate } = motion;

      cleanup = inView(
        el,
        () => {
          runScaleAppearAnimation(el, animate, startDelayS);
          window.setTimeout(() => {
            revealStaticElement(el);
          }, (startDelayS + SCALE_DURATION_S) * 1000 + 100);
          return undefined;
        },
        {
          amount: INVIEW_AMOUNT,
          margin: INVIEW_MARGIN,
        },
      );
    })
    .catch(() => {
      revealStaticElement(el);
    });

  cleanups.set(el, () => {
    cleanup();
    boundScaleElements.delete(el);
  });
}

function run() {
  document.querySelectorAll(SPLIT_SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) initSplitElement(el);
  });
  document.querySelectorAll(SCALE_SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) initScaleElement(el);
  });
}

whenReady(() => {
  requestAnimationFrame(() => requestAnimationFrame(run));
});

window.addEventListener("pageshow", (event) => {
  if (event.persisted) run();
});

document.addEventListener("shopify:section:load", (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;
  const splitEls = t.matches(SPLIT_SELECTOR)
    ? [t]
    : [...t.querySelectorAll(SPLIT_SELECTOR)];
  splitEls.forEach((el) => {
    if (el instanceof HTMLElement) initSplitElement(el);
  });
  const scaleEls = t.matches(SCALE_SELECTOR)
    ? [t]
    : [...t.querySelectorAll(SCALE_SELECTOR)];
  scaleEls.forEach((el) => {
    if (el instanceof HTMLElement) initScaleElement(el);
  });
});

document.addEventListener("shopify:section:unload", (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;
  const els = [
    ...(t.matches(SPLIT_SELECTOR) ? [t] : t.querySelectorAll(SPLIT_SELECTOR)),
    ...(t.matches(SCALE_SELECTOR) ? [t] : t.querySelectorAll(SCALE_SELECTOR)),
  ];
  els.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const fn = cleanups.get(el);
    if (typeof fn === "function") fn();
    cleanups.delete(el);
  });
});

export {};
